<template>
  <div class="foreground" style="height: 100%;">
    <!-- toolbar container, to prevent parent flexer shrinking children like hlines -->
    <div class="toolbar-pagetop">
      <v-container pa-0 style="position: relative;">
        <!-- PC TOOLBAR -->
        <div v-if="!$vuetify.breakpoint.xs">
          
          <v-layout class="pa-4 border-b--thin">
            <v-breadcrumbs class="pa-0">
              <v-breadcrumbs-item to="/dashboard/">
                Home
              </v-breadcrumbs-item>
              <span class="pl-2 pr-2">/</span>
              <v-breadcrumbs-item to="/productions">
                Productions
              </v-breadcrumbs-item>
              <span class="pl-2 pr-2">/</span>
              <v-breadcrumbs-item :to="`/production/${id}`" :exact="true">
                Production {{ id }}
              </v-breadcrumbs-item>
              <span class="pl-2 pr-2">/</span>
              <v-breadcrumbs-item disabled>
                output {{ outputId }}
              </v-breadcrumbs-item>
            </v-breadcrumbs>
          </v-layout>

          <v-layout align-center class="pa-4 border-b--thin">
            <div class="title">
              Production {{ id }} : 
              <span v-if="outputId">
                Output {{ outputId }}
              </span>
              <span v-else>
                Add output
              </span>
            </div>
            <v-spacer></v-spacer>
            <v-btn depressed
            class="subheading"
            text
            @click.native="save">
              SAVE
            </v-btn>
          </v-layout>

        </div>

        <!-- MOBILE toolbar -->
        <div v-if="$vuetify.breakpoint.xs">
          <v-layout align-center>
            <v-btn icon @click="drawer = !drawer">
              <v-icon>menu</v-icon>
            </v-btn>
            <v-flex class="text-ellipsed text-sans font-weight-bold">
              Add production output
            </v-flex>
          </v-layout>
        </div>
      </v-container>
    </div>
    <!-- end toolbar container, to prevent parent flexer shrinking children linke hlines -->

    <!-- MAIN CONTENT -->
    <div style="overflow: auto;" class="pb-4">
      <v-container>

        <v-row>
          <v-col cols="12" md="6">
            <v-card outlined class="elevation-0 mt-2">

              <v-layout align-center class="title font-weight-bold pa-4 border-b--thin">
                Output
              </v-layout>

              <div class="text--text text--darken-1 caption pa-2">
                *Outputs will be credited to your inventory on production completion
              </div>

              <div class="c-hr"></div>

              <v-layout wrap pa-2>

                <v-flex xs12>

                  <v-layout>
                    <v-flex xs3 class="left-label">
                      Output
                    </v-flex>
                    <v-flex xs9>
                      <v-layout 
                      @click="selectOutput"
                      class="crx-box-input pa-2" align-center>
                        <template v-if="!output">
                          Select
                        </template>
                        <template v-else>
                          <div class="">
                            {{ output.name }}
                          </div>
                        </template>
                        <v-spacer></v-spacer>
                        <v-icon>mdi-chevron-down</v-icon>
                      </v-layout>
                    </v-flex>
                  </v-layout>

                  <v-layout mt-3>
                    <v-flex xs3 class="left-label">
                      Build
                    </v-flex>
                    <v-flex xs9>
                      <v-layout 
                      @click="selectBuild"
                      class="crx-box-input pa-2" align-center>
                        <template v-if="!build">
                          <div v-if="builds.length > 0">
                            Select build
                          </div>
                          <div v-else>
                            Select a build
                          </div>
                        </template>
                        <template v-else>
                          <div class="">
                            {{ build.name }}
                          </div>
                        </template>
                        <v-spacer></v-spacer>
                        <v-btn icon text
                        v-if="build"
                        class="ma-0"
                        small
                        @click.stop="build = null">
                          <v-icon>
                            mdi-close
                          </v-icon>
                        </v-btn>
                        <v-icon>mdi-chevron-down</v-icon>
                      </v-layout>
                      <v-layout>
                        <div class="caption text--text text--darken-1 mt-1 mb-2">
                          {{ builds.length }} builds
                        </div>
                        <v-spacer></v-spacer>
                        <router-link
                        v-if="build"
                        class="caption"
                        style="text-decoration: none;"
                        :to="{ path: '/inventory/inventory-definition/build/' + build.id}" @click.native.stop>
                          View build
                        </router-link>
                      </v-layout>
                    </v-flex>
                  </v-layout>

                  <!-- QUANTITY -->

                  <v-layout mt-3>
                    <v-flex xs3 class="left-label">
                      Quantity
                    </v-flex>
                    <v-flex xs9>
                      <c-metric-selector
                      :inventoryItemID="(output || {}).id"
                      :key="(output || {}).id"
                      class="crx-box-input"
                      :realValue.sync="quantity">
                        
                      </c-metric-selector>
                      <c-transition-expand>
                        <div
                        v-if="build" 
                        class="caption pt-1 pb-2 text-xs-center">

                          <img class="c-align-self-start" src="@/assets/img/illustrations/recipe.png" alt="cardboard box" width="52px">
                          <div style="max-width: 250px;margin: auto;">
                            <v-progress-linear
                            class="ma-0"
                            v-model="percent"
                            height="5"
                            :color="percent < 90 ? 'green' : percent <= 100 ? 'amber' : 'red'"
                            ></v-progress-linear>
                          </div>

                          <div class="caption">
                            <span :class="[{'red--text': quantity > maxQuantity}]">
                              {{ quantity }} / {{ maxQuantity }}
                            </span>
                            <div v-if="quantity > maxQuantity"
                              class="red--text text--darken-1">
                              * You only have enough inventory to make
                              <c-metric-selector
                              :inventoryItemID="(output || {}).id"
                              :realValue="maxQuantity"
                              :key="(output || {}).id"
                              alwaysScale
                              labelMode
                              class="text-sans font-weight-medium"
                              ></c-metric-selector>
                              using this recipe
                            </div>
                            <v-chip v-if="quantity > maxQuantity" color="red" text-color="white">
                              + &nbsp;
                              <c-metric-selector
                              :inventoryItemID="(output || {}).id"
                              :realValue="quantity - maxQuantity"
                              :key="(output || {}).id"
                              alwaysScale
                              labelMode
                              class="text-sans"
                              ></c-metric-selector>
                            </v-chip>
                          </div>
                        </div>
                      </c-transition-expand>
                    </v-flex>
                  </v-layout>

                  <v-layout mt-3>
                    <v-flex xs3 class="left-label">
                      Final Yield
                    </v-flex>
                    <v-flex xs9>
                      <c-metric-selector
                      :inventoryItemID="(output || {}).id"
                      :key="(output || {}).id"
                      class="crx-box-input"
                      :realValue.sync="finalYield">
                        
                      </c-metric-selector>
                    </v-flex>
                  </v-layout>

                  <v-layout mt-3>
                    <v-flex xs3 class="left-label">
                      Batch number
                    </v-flex>
                    <v-flex xs9>
                      <c-input :value.sync="batchNumber" class="crx-box-input"></c-input>
                    </v-flex>
                  </v-layout>

                  <v-layout mt-3>
                    <v-flex xs3 class="left-label">
                      Expiry date
                    </v-flex>
                    <v-flex xs9>
                      <c-date 
                      class="crx-box-input"
                      :date.sync="expiryDate">
                      </c-date>
                      <div class="caption text--text text--darken-1">
                        {{ expiryFromNow }}
                      </div>
                      <recent-batch-expiries :expiryDate.sync="expiryDate"></recent-batch-expiries>
                    </v-flex>
                  </v-layout>
                  
                </v-flex>

              </v-layout>

            </v-card>
          </v-col>
          <v-col cols="12" md="6">
            <v-card outlined class="elevation-0 mt-2">

              <v-layout align-center class="title font-weight-bold pa-4 border-b--thin">
                Inputs
                <v-spacer></v-spacer>
                <v-switch
                v-model="automaticallySelectInputs"
                label="Auto"
                color="green"
                class="shrink mt-0"
                hide-details
                ></v-switch>

              </v-layout>
              
              <div class="text--text text--darken-1 caption text-sans pa-2">
                *Inputs will be removed from inventory immediately and reserved for this production
              </div>

              <div class="c-hr"></div>

              <c-transition-expand>
                <div
                v-if="showRecipeErrors && !recipeFilledPerfectly"
                class="text-xs-center">
                  <v-icon 
                  color="amber"
                  style="font-size: 5rem;">
                    warning
                  </v-icon>
                  <br>
                  <div
                  class="text--text text--darken-1 caption">
                    Your debiting different quantities than what the recipe requires
                  </div>
                  <v-btn 
                  depressed 
                  outline
                  color="green"
                  :disabled="recipeFilledPerfectly"
                  @click="autoCalculateInputs">
                  <v-layout align-center>
                    <img class="c-align-self-start" src="@/assets/img/illustrations/recipe.png" alt="cardboard box" width="32px">
                    Recalculate
                  </v-layout>
                  </v-btn>
                  <div class="c-hr"></div>
                </div>
              </c-transition-expand>


              <!-- DEBITS -->
              <div class="title font-weight-bold mt-4 mb-3 ml-2 text--text text--darken-1">
                Raw materials
              </div>
              <debits 
              :itemsSource="itemsRM" 
              :inputs="inputsRM" 
              :requiredDict="requiredDictRM"
              :totalFilledDict="totalFilledDictRM" 
              :quantity="quantity"
              :showRecipeErrors="showRecipeErrors"
              :inventoryView="inventoryView"></debits>

              <div class="title font-weight-bold mt-4 mb-3 ml-2 text--text text--darken-1">
                Packaging
              </div>
              <debits 
              :itemsSource="itemsPKG" 
              :inputs="inputsPKG" 
              :requiredDict="requiredDictPKG"
              :totalFilledDict="totalFilledDictPKG" 
              :quantity="quantity"
              :showRecipeErrors="showRecipeErrors"
              :inventoryView="inventoryView"></debits>

            </v-card>
          </v-col>
        </v-row>

      </v-container>
    </div><!-- END MAIN CONTENT -->

    <!-- DIALOGS -->
    <c-select-inv-def-dialog ref="selectItemDialog" :currentSelectedID="(output || {}).id">
    </c-select-inv-def-dialog>

    <select-build-dialog
    ref="selectBuildDialog"
    :inventoryItemId="output ? output.id : null"></select-build-dialog>

  </div>
</template>

<script>
import SelectBuildDialog from '@/components/dialogs/select-build/Dialog'
import Debits from './components/inputs/Main'
import RecentBatchExpiries from './components/recent-batch-expiry/main'
import Vue from 'vue'

export default {
  props: ['id', 'outputId'],
  components: {
    SelectBuildDialog,
    Debits,
    RecentBatchExpiries
  },
  data () {
    return {
      itemsRM: [],
      itemsPKG: [],
      editMode: this.outputId !== undefined,
      output: null,
      build: null,
      quantity: 0,
      expiryDate: null,
      batchNumber: null,
      finalYield: null,
      inputsRM: {},
      inputsPKG: {},
      automaticallySelectInputs: true
    }
  },
  watch: {
    build (nv) {
      if (this.automaticallySelectInputs) {
        this.autoCalculateInputs()
      }
    },
    quantity (nv) {
      if (this.automaticallySelectInputs) {
        this.autoCalculateInputs()
      }
    },
    automaticallySelectInputs (nv) {
      if (nv) {
        this.autoCalculateInputs()
      }
    }
  },
  computed: {
    /*
    Create View table
    */
    debitTable () {
      // Simply clone and return productionInputs [] for this output item
      var outputId = parseInt(this.outputId)
      var storeDebits = Object.values(this.$store.state.db.productionInputs.productionInputs).filter(item => {
        return item.production_output_id === outputId
      })
      return JSON.parse(JSON.stringify(storeDebits))
    },
    debitTableRM () {
      return this.debitTable.filter(item => item.type === 0)
    },
    debitTablePKG () {
      return this.debitTable.filter(item => item.type === 1)
    },
    mergeMap () {
      var dict = {}
      var idGenerator = 0
      for (var debit of this.debitTable) {
        // check if there is a matching archive_id in Inventory
        var inventory = Object.values(this.$store.state.db.inventory.inventory).filter(item => {
          return item.archive_id === debit.archive_id
        })
        // if theres a matching inv item to re-add the stock to
        if (inventory.length > 0) {
          dict[debit.id] = inventory[0].id
        } else {
          idGenerator -= 1
          dict[debit.id] = idGenerator
        }
      }
      return dict
    },
    inventoryView () {
      // this is current inventory with debits readded
      var inventoryView = JSON.parse(JSON.stringify(this.$store.state.db.inventory.inventory))
      for (var debit of this.debitTable) {
        var invMergeId = this.mergeMap[debit.id]
        var invItem = inventoryView[invMergeId]
        if (invItem) {
          invItem.quantity += debit.quantity
        } else {
          // recreate the missing item in inventoryView
          inventoryView[invMergeId] = {
            id: invMergeId,
            inventory_item_id: debit.inventory_item_id,
            archive_id: debit.archive_id,
            quantity: debit.quantity
          }
        }
      }
      return inventoryView
    },
    buildItems () {
      return this.$store.getters['db/productBuilds/getBuildItems']((this.build || {}).id)
    },
    buildItemsRM () {
      return this.buildItems.filter(item => item.type === 0)
    },
    buildItemsPKG () {
      return this.buildItems.filter(item => item.type === 1)
    },
    builds () {
      return this.$store.getters['db/productBuilds/getProductBuilds']((this.output || {}).id)
    },
    requiredDictRM () {
      var dict = {}
      for (var item of this.buildItemsRM) {
        var quantity = item.quantity * this.quantity
        dict[item.inventory_item_id] = (dict[item.inventory_item_id] || 0) + (quantity)
      }
      return dict
    },
    requiredDictPKG () {
      var dict = {}
      for (var item of this.buildItemsPKG) {
        var quantity = item.quantity * this.quantity
        dict[item.inventory_item_id] = (dict[item.inventory_item_id] || 0) + (quantity)
      }
      return dict
    },
    totalFilledDictRM () {
      var dict = {}
      for (var definitionId of Object.keys(this.inputsRM)) { // key = definitionId
        dict[definitionId] = Object.values(this.inputsRM[definitionId]).reduce((a, i) => a + i, 0)
      }
      return dict
    },
    totalFilledDictPKG () {
      var dict = {}
      for (var definitionId of Object.keys(this.inputsPKG)) { // key = definitionId
        dict[definitionId] = Object.values(this.inputsPKG[definitionId]).reduce((a, i) => a + i, 0)
      }
      return dict
    },
    totalInStockDict () {
      var dict = {}
      for (var item of this.buildItems) {
        var inventory = Object.values(this.inventoryView).filter(invItem => invItem.inventory_item_id === item.inventory_item_id)
        dict[item.inventory_item_id] = inventory.reduce((a, b) => +a + +b.quantity, 0)
      }
      return dict
    },
    recipeFilledPerfectly () {
      var validRM = JSON.stringify(this.requiredDictRM) === JSON.stringify(this.totalFilledDictRM)
      var validPKG = JSON.stringify(this.requiredDictPKG) === JSON.stringify(this.totalFilledDictPKG)
      return validRM && validPKG
    },
    maxQuantityDict () {
      var dict = {}
      for (var item of this.buildItems) {
        var max = Math.floor(this.totalInStockDict[item.inventory_item_id] / item.quantity)
        dict[item.inventory_item_id] = max
      }
      return dict
    },
    maxQuantity () {
      if (Object.keys(this.maxQuantityDict).length < 1) {
        return 0
      }
      return Object.values(this.maxQuantityDict).reduce((a, i) => i < a ? i : a)
    },
    percent () {
      var percent = 100 - (((this.maxQuantity - this.quantity) / this.maxQuantity) * 100)
      return isNaN(percent) ? 0 : percent
    },
    showRecipeErrors () {
      return (this.build !== null) && (this.quantity !== 0)
    },
    expiryFromNow () {
      if (this.$moment.utc(this.expiryDate).isValid()) {
        return this.$moment.utc(this.expiryDate).fromNow()
      }
      return ''
    }
  },
  methods: {
    selectOutput () {
      this.$refs.selectItemDialog.open()
        .then((selectedItem) => {
          this.output = selectedItem
          var builds = this.$store.getters['db/productBuilds/getProductBuilds'](selectedItem.id)
          if (builds.length > 0) {
            this.build = builds[0]
          } else {
            this.build = null
          }
          this.quantity = 0
        })
        .catch(() => {
          // console.log('rejected')
        })
    },
    selectBuild () {
      this.$refs.selectBuildDialog.open((this.output || {}).id)
        .then((selectedItem) => {
          this.build = selectedItem
        })
        .catch(() => {
          // console.log('rejected')
        })
    },
    pruneAndPushExactBuildIds (itemArray, buildIdMap) {
      // remove items from itemArray no longer needed and push ids of to be added items
      var itemIDMap = itemArray.map(item => item.definitionId)
      var missingFromItems = buildIdMap.filter(i => !itemIDMap.includes(i))
      for (var id of missingFromItems) {
        itemArray.push({
          definitionId: id
        })
      }
      // remove items no longer in use
      return itemArray.filter(item => buildIdMap.includes(item.definitionId))
    },
    recalculateInputs (inputObj, buildItems, requiredDict) {
      for (var item of buildItems) {
        Vue.set(inputObj, item.inventory_item_id, {})
        var required = requiredDict[item.inventory_item_id]
        var inventory = Object.values(this.inventoryView).filter(invItem => invItem.inventory_item_id === item.inventory_item_id)
        // sort inventory
        inventory.sort((a, b) => {
          var expiryA = this.$store.state.db.inventoryArchive.archive[a.archive_id].expiry_date
          var expiryB = this.$store.state.db.inventoryArchive.archive[b.archive_id].expiry_date
          // console.log(new Date(expiryA) + ' ' + new Date(expiryB))
          return new Date(expiryA) - new Date(expiryB)
        })
        // And now we loop fill
        for (var invItem of inventory) {
          if (invItem.quantity >= required) { // if item has more quantity than what is required
            Vue.set(inputObj[item.inventory_item_id], invItem.id, required)
            required -= required
          } else {
            Vue.set(inputObj[item.inventory_item_id], invItem.id, invItem.quantity)
            required -= invItem.quantity
          }
          // break out if completely filled
          if (this.required <= 0) {
            break
          }
        }
      }
    },
    autoCalculateInputs () {
      this.inputsRM = {}
      this.itemsRM = this.pruneAndPushExactBuildIds(this.itemsRM, this.buildItemsRM.map(item => item.inventory_item_id))
      this.recalculateInputs(this.inputsRM, this.buildItemsRM, this.requiredDictRM)

      this.inputsPKG = {}
      this.itemsPKG = this.pruneAndPushExactBuildIds(this.itemsPKG, this.buildItemsPKG.map(item => item.inventory_item_id))
      this.recalculateInputs(this.inputsPKG, this.buildItemsPKG, this.requiredDictPKG)
    },
    save () {
      if (this.editMode) {
        this.editOutput()
        .catch(error => { this.$root.$errorSnackbar(error) })
      } else {
        this.addOutput()
        .catch(error => { this.$root.$errorSnackbar(error) })
      }
    },
    addOutput () {
      return new Promise((resolve, reject) => {
        var data = {
          productionId: this.id,
          buildID: (this.build || {}).id,
          batchNumber: this.batchNumber,
          expiryDate: this.expiryDate,
          inventory_item_id: this.output.id,
          quantity: this.quantity,
          finalYield: this.finalYield || 0,
          inputsRM: this.inputsRM,
          inputsPKG: this.inputsPKG
        }
        console.log(data)
        this.$axios.post('productions/add-output',
          data,
          {headers: {'Content-Type': 'application/json'}})
          .then(response => {
            this.$router.push(`/production/${this.id}`)
            resolve()
          })
          .catch(error => {
            this.$root.$errorSnackbar(error)
            reject(error)
          })
      })
    },
    editOutput () {
      return new Promise((resolve, reject) => {
        var data = {
          id: this.outputId,
          productionId: this.id,
          buildID: (this.build || {}).id,
          batchNumber: this.batchNumber,
          expiryDate: this.expiryDate,
          inventory_item_id: this.output.id,
          quantity: this.quantity,
          finalYield: this.finalYield || 0,
          inputsRM: this.inputsRM,
          inputsPKG: this.inputsPKG,
          mergeMap: this.mergeMap
        }
        console.log(data)
        this.$axios.post('productions/edit-output',
          data,
          {headers: {'Content-Type': 'application/json'}})
          .then(response => {
            this.$router.push(`/production/${this.id}`)
            resolve()
          })
          .catch(error => {
            this.$root.$errorSnackbar(error)
            reject(error)
          })
      })
    },
    initRM () {
      for (var input of this.debitTableRM) {
        if (!this.inputsRM[input.inventory_item_id]) {
          Vue.set(this.inputsRM, input.inventory_item_id, {})
        }
        var invMergeId = this.mergeMap[input.id]
        var currentDebit = this.inputsRM[input.inventory_item_id][invMergeId] || 0
        Vue.set(this.inputsRM[input.inventory_item_id], invMergeId, currentDebit + input.quantity)
      }
      for (var definitionId of Object.keys(this.inputsRM)) {
        this.itemsRM.push({
          definitionId: parseInt(definitionId)
        })
      }
    },
    initPKG () {
      for (var input of this.debitTablePKG) {
        if (!this.inputsPKG[input.inventory_item_id]) {
          Vue.set(this.inputsPKG, input.inventory_item_id, {})
        }
        var invMergeId = this.mergeMap[input.id]
        var currentDebit = this.inputsPKG[input.inventory_item_id][invMergeId] || 0
        Vue.set(this.inputsPKG[input.inventory_item_id], invMergeId, currentDebit + input.quantity)
      }
      for (var definitionId of Object.keys(this.inputsPKG)) {
        this.itemsPKG.push({
          definitionId: parseInt(definitionId)
        })
      }
    }
  },
  mounted () {
    if (this.editMode) {
      var productionOutput = this.$store.state.db.productionOutputs.productionOutputs[this.outputId]
      var definition = this.$store.state.db.inventoryDefinitions.definitions[productionOutput.inventory_item_id]
      this.output = definition
      this.expiryDate = productionOutput.expiry_date
      this.batchNumber = productionOutput.batch_number
      this.finalYield = productionOutput.yield
      this.build = this.$store.state.db.productBuilds.builds[productionOutput.product_build]
      this.build = typeof this.build === 'undefined' ? null : this.build
      this.quantity = productionOutput.quantity
      this.initRM()
      this.initPKG()
      this.automaticallySelectInputs = false
    }
  }
}
</script>

<style scoped lang="scss">
.left-label {
  padding-top: 10px;
  padding-right: 16px;
  text-align: right;
  font-size: 14px;
}
</style>
